/**
 * vocabulary.ts — Features → ControlState. Pure function of its inputs: same
 * fixture in, same ControlState out, every time.
 *
 * Implements the discrete table from docs/gesture-vocabulary.md, top to bottom,
 * first match wins: fist suppresses pinch on the same hand; pinch on the
 * expression hand arms a note; pinch on the pitch hand holds an octave up.
 *
 * This is the ONLY place discrete gesture decisions are made. Scenes consume
 * the resulting ControlState and never look at raw input events — one
 * implementation of the hysteresis, one owner of the latch, one place to test.
 */

import { degreeFromHeight } from '../audio/common/pitch';
import { hysteresis } from './filters';
import type { ControlState, FeatureFrame, TriggerEvent } from './types';

export interface VocabConfig {
  pinchOn: number;
  pinchOff: number;
  /** ms a pose must persist before it takes effect. */
  dwellMs: number;
  /** ms a pose may vanish for and still count as held. */
  nullWindowMs: number;
  /** ms of missing/unconfident pitch hand before the degree latches. */
  dropoutMs: number;
  /** ms both open palms must hold still before panic fires. */
  panicMs: number;
  degrees: number;
  degreeHysteresis: number;
}

export const DEFAULT_VOCAB: VocabConfig = {
  pinchOn: 0.35, pinchOff: 0.5,
  dwellMs: 100, nullWindowMs: 50, dropoutMs: 100, panicMs: 600,
  degrees: 7, degreeHysteresis: 0.25,
};

export interface VocabMemory {
  pinchExpr: boolean;
  pinchPitch: boolean;
  fist: boolean;
  /** ms the fist pose has persisted while not yet engaged. */
  fistHeldMs: number;
  /** ms the fist pose has been absent while still engaged. */
  fistGoneMs: number;
  degree: number;
  octave: number;
  latched: boolean;
  /** ms the pitch hand has been missing. */
  missingMs: number;
  /** ms both open palms have held still. */
  panicMs: number;
  /** Panic fired for this hold; the pose must exit before it can re-arm. */
  panicSpent: boolean;
  prevExprHeight: number;
  prevPitchHeight: number;
}

export const freshMemory = (): VocabMemory => ({
  pinchExpr: false, pinchPitch: false,
  fist: false, fistHeldMs: 0, fistGoneMs: 0,
  degree: 0, octave: 0, latched: false, missingMs: 0,
  panicMs: 0, panicSpent: false, prevExprHeight: 0, prevPitchHeight: 0,
});

export interface VocabResult {
  state: ControlState;
  memory: VocabMemory;
}

export function applyVocabulary(
  frame: FeatureFrame,
  mem: VocabMemory,
  cfg: VocabConfig = DEFAULT_VOCAB,
): VocabResult {
  const m: VocabMemory = { ...mem };
  const triggers: TriggerEvent[] = [];
  const e = frame.expr;

  // Evaluate the discrete table top to bottom. Fist first: a fist satisfies
  // the pinch condition too, so damping would otherwise fire a note on the way
  // in. Detection is the doc's pose — all fingertips within 0.6 of the palm
  // centroid, computed in features.ts — and a POSE debounces in the time
  // domain (invariant 5): it must persist dwellMs to engage, and be gone past
  // the null window to release, which absorbs single-frame tracker flicker
  // that no threshold gap can touch. The old detection was an undocumented
  // aperture proxy with halved constants; the doc's fist was dead code.
  if (e.present && e.fistPose) {
    m.fistGoneMs = 0;
    if (!m.fist) {
      m.fistHeldMs += frame.dt * 1000;
      if (m.fistHeldMs >= cfg.dwellMs) m.fist = true;
    }
  } else {
    m.fistHeldMs = 0;
    if (m.fist) {
      m.fistGoneMs += frame.dt * 1000;
      if (m.fistGoneMs >= cfg.nullWindowMs) { m.fist = false; m.fistGoneMs = 0; }
    }
  }
  const fist = m.fist;

  const pinch = fist ? false : hysteresis(e.aperture, m.pinchExpr, cfg.pinchOn, cfg.pinchOff);
  if (pinch && !m.pinchExpr) triggers.push({ kind: 'note', index: m.degree, velocity: 1 });
  m.pinchExpr = pinch;

  // The pitch hand: degree by height with boundary hysteresis, octave by pinch.
  // The dropout contract applies ONLY to a source that can carry a pitch hand.
  // A source with no pitch channel (the pad) must not walk into the dropout
  // branch on every frame — that is how `latched` was once permanently true
  // and the degree froze at 0 for every pad user.
  const p = frame.pitch;
  if (p?.present && p.confident) {
    m.missingMs = 0;
    m.latched = false;
    m.degree = degreeFromHeight(p.height, m.degree, cfg.degrees, cfg.degreeHysteresis);
    const pp = hysteresis(p.aperture, m.pinchPitch, cfg.pinchOn, cfg.pinchOff);
    m.pinchPitch = pp;
    m.octave = pp ? 1 : 0;
  } else if (frame.sourceHasPitch) {
    // Dropout LATCHES, never resets. Confidence below threshold for longer
    // than dropoutMs counts as gone; the degree freezes at its last confident
    // value, and a held octave does not outlive the hand holding it.
    m.missingMs += frame.dt * 1000;
    if (m.missingMs > cfg.dropoutMs) {
      m.latched = true;
      m.pinchPitch = false;
      m.octave = 0;
    }
  }

  // Panic: BOTH hands open palm, still, for panicMs, then re-arm only after
  // the pose exits. Two-hand by design — during play the two hands are never
  // doing the same thing, which is what keeps this unreachable mid-phrase.
  // An open palm cannot satisfy the pinch threshold, so panic never races a
  // trigger on the same frame. Sources without a pitch channel have no panic
  // gesture; the pad's escape is its own UI.
  let panic = false;
  const openPalm = (h: { present: boolean; fistPose: boolean; aperture: number; spread: number }): boolean =>
    h.present && !h.fistPose && h.aperture > 0.7 && h.spread > 0.55;
  if (frame.sourceHasPitch && openPalm(e) && p !== undefined && openPalm(p)) {
    const still =
      Math.abs(e.height - m.prevExprHeight) + Math.abs(p.height - m.prevPitchHeight) < 0.02;
    m.panicMs = still ? m.panicMs + frame.dt * 1000 : 0;
    if (m.panicMs >= cfg.panicMs && !m.panicSpent) {
      panic = true;
      m.panicSpent = true;
    }
  } else {
    m.panicMs = 0;
    m.panicSpent = false; // pose exited — re-armed
  }
  m.prevExprHeight = e.height;
  if (p) m.prevPitchHeight = p.height;

  const state: ControlState = {
    degree: m.degree,
    octave: m.octave,
    latched: m.latched,
    fist,
    pinch,
    panic,
    triggers,
  };
  return { state, memory: m };
}
